import React, { useEffect, useRef, useState } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import { IconButton, ITextField, TextField } from '@fluentui/react';

import { Key } from 'src/constants/index';

import { observer } from 'mobx-react';
import { contextStore } from 'src/stores';
import { useCellAddressFormatter } from './hooks/useCellAddressFormatter';
import { useCellAddressSelection } from './hooks/useCellAddressSelection';
import { useCellAddressValidation } from './hooks/useCellAddressValidation';
import { getClassNames, pickerIconProps, textFieldStyles } from './styles';
import { CellAddressPickerValue } from './types';
import SpanWithTooltip from 'src/components/base/SpanWithTooltip';



interface CellAddressPickerProps {
  value: CellAddressPickerValue | null;
  onChange: (value: CellAddressPickerValue | null, valid: boolean) => void;
  disabled: boolean;
  label?: string;
  tooltip?: string;
  allowEmptyCell?: boolean;
  allowTargetPicker?: boolean;
  disableSingleCell?: boolean;
  allowNewSheet?: boolean;
  localAddressSheetIdGetter: () => string;
}

const CellAddressPicker: React.FC<CellAddressPickerProps> = ({
  value,
  onChange,
  disabled,
  label = '',
  tooltip = '',
  allowEmptyCell = false,
  allowTargetPicker = true,
  disableSingleCell = false,
  allowNewSheet = false,
  localAddressSheetIdGetter
}) => {

  const {
    errorMessage,
    setErrorMessage,
    validateInput,
    getWorksheet,
    getCustomRange
  } = useCellAddressValidation(contextStore, allowEmptyCell, allowNewSheet);

  const { buildInputValue } = useCellAddressFormatter(getWorksheet);

  const [inputValue, setInputValue] = useState<string>(() => buildInputValue(value));

  const { handleTargetClick } = useCellAddressSelection(
    disableSingleCell,
    onChange,
    buildInputValue,
    setInputValue,
    setErrorMessage,
  );


  useEffectOnce(() => {
    let valid = value || allowEmptyCell;
    if (value) {
      const rangeValidaton = getCustomRange(value.customRange);
      if (!rangeValidaton.isValid) {
        valid = false;
        setErrorMessage("Named range not found");
      } else {
        const worksheetValidation = getWorksheet({ id: value.worksheetId });
        if (!worksheetValidation.isValid) {
          valid = false;
          setErrorMessage("Worksheet not found");
        }
      }
    }
    if (!valid) {
      onChange(value, Boolean(valid));
    }
  });

  useEffect(() => {
    if (value !== null) {
      setInputValue(buildInputValue(value));
    }
  }, [value]);

  useUpdateEffect(() => {
    if (value) {
      const worksheetValidation = getWorksheet({ id: value.worksheetId });
      setErrorMessage(worksheetValidation.isValid ? null : "Worksheet not found");
      onChange(value, worksheetValidation.isValid);
    }
  }, [allowNewSheet]);

  useUpdateEffect(() => {
    const valid = value || allowEmptyCell;
    if (!valid) {
      onChange(value, Boolean(valid));
    }
  }, [allowEmptyCell]);


  function onBlur(): void {
    if (value?.address && !value.worksheetId) {
      const newValue = { ...value };
      const sheetId = localAddressSheetIdGetter();
      newValue.worksheetId = sheetId;
      setInputValue(buildInputValue(newValue));
      onChange(newValue, true);
    }
  }

  function onValueChange(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const value = e.currentTarget.value;
    setInputValue(value);
    const validation = validateInput(value);
    const newValue: CellAddressPickerValue | null =
      validation.isEmpty || !validation.isValid
        ? null
        : {
          customRange: validation.rangeName || '',
          worksheetId: validation.worksheetId || '',
          address: validation.address || '',
        };
    onChange(newValue, validation.isValid);
  }

  const inputRef = useRef<ITextField>(null);

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if ([Key.Enter, Key.Escape].includes(event.key)) {
      inputRef.current?.blur();
    }
  }

  const classNames = getClassNames(!!errorMessage);

  return (
    <div className={classNames.root}>
      {label && <SpanWithTooltip label={label} tooltip={tooltip} className={undefined} />}
      <div className={classNames.textField}>
        {allowTargetPicker &&
          <IconButton
            disabled={disabled}
            iconProps={pickerIconProps}
            onClick={handleTargetClick}
          />
        }
        <TextField
          // @ts-ignore
          componentRef={inputRef}
          styles={textFieldStyles}
          disabled={disabled}
          value={inputValue}
          onChange={onValueChange}
          onBlur={onBlur}
          onKeyDown={handleInputKeyDown}
          errorMessage={errorMessage || undefined}
        />
      </div>
    </div>
  );
};

export default observer(CellAddressPicker);